/**
 * @file Debounced, abortable airport/city search against `search-airport/v3`.
 *
 * Keeps the previous result set visible while a refined query is in flight so
 * typing does not wipe the dropdown on every keystroke — `isRefining`
 * distinguishes that from a cold `isLoading`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedValue, useNetwork } from '@mantine/hooks';

import { searchAirports, ABORTED, MIN_QUERY_LENGTH, DEFAULT_LIMIT, SEARCH_DEBOUNCE_MS } from 'utils/airport-search';

/**
 * @param {object}   options
 * @param {string}   options.query       Raw (undebounced) search text.
 * @param {number}  [options.limit]      Max results, 1–25.
 * @param {string}  [options.mode]       `all` or `us_only`.
 * @param {boolean} [options.enabled]    Set false to suspend searching entirely
 *   (e.g. a multi-select that has hit its limit).
 * @param {number}  [options.minLength]  Characters required before we hit the API.
 */
export function useAirportSearch({ query, limit = DEFAULT_LIMIT, mode, enabled = true, minLength = MIN_QUERY_LENGTH }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  /** The query the current `results` actually belong to. */
  const [resolvedQuery, setResolvedQuery] = useState('');
  /** Bumped by `retry()`. Without it the effect keys only on the query, so a
   *  failure is terminal until the user edits their text. */
  const [attempt, setAttempt] = useState(0);

  const [debouncedQuery] = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const { online } = useNetwork();

  // Monotonic token: only the newest request may write state. Guards against a
  // slow fuzzy-match response (~75ms) landing after a fast prefix one (~35ms).
  const requestId = useRef(0);

  const trimmed = (debouncedQuery || '').trim();
  const isBelowMin = trimmed.length < minLength;

  useEffect(() => {
    if (!enabled || isBelowMin) {
      requestId.current += 1;
      setResults([]);
      setResolvedQuery('');
      setLoading(false);
      setError(null);
      return undefined;
    }

    requestId.current += 1;
    const id = requestId.current;
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    searchAirports({ q: trimmed, limit, mode, signal: controller.signal })
      .then((payload) => {
        if (payload === ABORTED || id !== requestId.current) return;
        setResults(payload.results);
        setResolvedQuery(payload.query);
        setLoading(false);
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        setError(err);
        setResults([]);
        setLoading(false);
      });

    return () => controller.abort();
  }, [trimmed, limit, mode, enabled, isBelowMin, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  /**
   * A 401 means the token expired (v3 returns 401 where v2 returned 400/500).
   * `callAzureFunction`'s interceptor already force-logs-out on 401, so this is
   * only for wording the message while that redirect happens.
   */
  const errorStatus = error?.response?.status ?? null;

  return {
    results,
    error,
    errorStatus,
    /** True when the failure is a lost connection rather than a server fault. */
    isOffline: !online,
    retry,
    /** Query the visible `results` correspond to — use for highlighting. */
    resolvedQuery,
    /** True while typing, before the debounce settles. Gate `aria-live` on this
     *  only — binding a spinner to it flashes on every keystroke. */
    isPending: (query || '').trim() !== trimmed,
    /** First load for this query: nothing to show yet. */
    isLoading: loading && results.length === 0,
    /** Refining an existing list: keep the old rows, show a subtle indicator. */
    isRefining: loading && results.length > 0,
    /** Query is too short to search — prompt rather than say "no results". */
    isBelowMin,
    /** Searched and genuinely found nothing (a normal 200, not an error). */
    isEmpty: !loading && !error && !isBelowMin && results.length === 0 && resolvedQuery !== ''
  };
}

export default useAirportSearch;
